// Генерация товарной накладной (PDF) в кабинете поставщика.
// Векторный PDF через jsPDF + autotable, кириллица — встроенный шрифт PT Sans.
// Шрифт грузится по требованию (только при первой генерации), чтобы не утяжелять бандл.

import type { jsPDF } from 'jspdf';

import type { SupplierOrder } from '@/services/orders';
import type { SupplierProfile } from '@/services/profile';

const FONT_FAMILY = 'PTSans';

// base64 шрифтов кэшируем в модуле — грузим один раз за сессию.
let fontCache: { regular: string; bold: string } | null = null;

async function arrayBufferToBase64(buf: ArrayBuffer): Promise<string> {
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000; // по кускам, иначе стек переполняется на больших файлах
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function loadFonts(): Promise<{ regular: string; bold: string }> {
  if (fontCache) return fontCache;
  const base = import.meta.env.BASE_URL || '/';
  const [reg, bold] = await Promise.all([
    fetch(`${base}fonts/PTSans-Regular.ttf`).then((r) => r.arrayBuffer()),
    fetch(`${base}fonts/PTSans-Bold.ttf`).then((r) => r.arrayBuffer()),
  ]);
  fontCache = {
    regular: await arrayBufferToBase64(reg),
    bold: await arrayBufferToBase64(bold),
  };
  return fontCache;
}

function registerFonts(doc: jsPDF, fonts: { regular: string; bold: string }): void {
  doc.addFileToVFS('PTSans-Regular.ttf', fonts.regular);
  doc.addFont('PTSans-Regular.ttf', FONT_FAMILY, 'normal');
  doc.addFileToVFS('PTSans-Bold.ttf', fonts.bold);
  doc.addFont('PTSans-Bold.ttf', FONT_FAMILY, 'bold');
  doc.setFont(FONT_FAMILY, 'normal');
}

// ── Форматирование ───────────────────────────────────────────────────────────
const money = (kop: number): string =>
  (kop / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const qtyFmt = (q: number): string =>
  q.toLocaleString('ru-RU', { maximumFractionDigits: 3 });

const longDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

export const invoiceNo = (order: SupplierOrder): string => order.id.slice(-6).toUpperCase();

export const invoiceFileName = (order: SupplierOrder): string =>
  `Накладная ${invoiceNo(order)}.pdf`;

// «От кого» — реквизиты поставщика одной строкой.
function supplierLine(s: SupplierProfile | null): string {
  if (!s) return '—';
  const name = s.legalName?.trim() || s.name;
  const extra = [s.inn ? `ИНН ${s.inn}` : null, s.legalAddress?.trim() || null]
    .filter(Boolean)
    .join(', ');
  return extra ? `${name}, ${extra}` : name;
}

// «Кому» — реквизиты заведения-получателя.
function customerLine(order: SupplierOrder): string {
  const e = order.establishment;
  const extra = [
    order.deliveryAddress?.trim() || e.address?.trim() || null,
    order.contactPhone?.trim() || e.phone?.trim() || null,
  ]
    .filter(Boolean)
    .join(', ');
  return extra ? `${e.name}, ${extra}` : e.name;
}

// ── Построение документа ─────────────────────────────────────────────────────
export async function buildInvoice(
  order: SupplierOrder,
  supplier: SupplierProfile | null,
): Promise<jsPDF> {
  // Ленивая загрузка тяжёлой PDF-машинерии — только при формировании накладной.
  const [{ jsPDF }, autoTableMod, fonts] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    loadFonts(),
  ]);
  const autoTable = autoTableMod.default;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  registerFonts(doc, fonts);

  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentW = pageW - margin * 2;

  // Дата — справа сверху
  doc.setFont(FONT_FAMILY, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`от ${longDate(order.createdAt)}`, pageW - margin, 18, { align: 'right' });

  // Заголовок
  doc.setFont(FONT_FAMILY, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(20);
  doc.text(`НАКЛАДНАЯ № ${invoiceNo(order)}`, pageW / 2, 30, { align: 'center' });

  // Кому / От кого
  let y = 42;
  const labelValue = (label: string, value: string) => {
    doc.setFont(FONT_FAMILY, 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(20);
    doc.text(label, margin, y);
    const labelW = doc.getTextWidth(label) + 2;
    doc.setFont(FONT_FAMILY, 'normal');
    doc.setTextColor(40);
    const lines = doc.splitTextToSize(value, contentW - labelW);
    doc.text(lines, margin + labelW, y);
    y += lines.length * 5 + 3;
  };
  labelValue('Кому:', customerLine(order));
  labelValue('От кого:', supplierLine(supplier));

  // Таблица позиций
  const body = order.items.map((it, i) => [
    String(i + 1),
    it.name,
    `${qtyFmt(it.quantity)} ${it.unit}`,
    money(it.price),
    money(it.lineTotal),
  ]);

  autoTable(doc, {
    startY: y + 2,
    margin: { left: margin, right: margin },
    head: [['№', 'Наименование', 'Кол-во', 'Цена, ₽', 'Сумма, ₽']],
    body,
    foot: [
      [
        { content: 'Итого', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: money(order.totalCost), styles: { halign: 'right', fontStyle: 'bold' } },
      ],
    ],
    styles: { font: FONT_FAMILY, fontSize: 9.5, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: 30 },
    headStyles: { font: FONT_FAMILY, fontStyle: 'bold', fillColor: [243, 244, 246], textColor: 30, halign: 'center' },
    footStyles: { font: FONT_FAMILY, fillColor: [255, 255, 255], textColor: 20 },
    columnStyles: {
      0: { halign: 'center', cellWidth: 12 },
      1: { halign: 'left' },
      2: { halign: 'right', cellWidth: 28 },
      3: { halign: 'right', cellWidth: 28 },
      4: { halign: 'right', cellWidth: 30 },
    },
    theme: 'grid',
  });

  // Подписи
  const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  const pageH = doc.internal.pageSize.getHeight();
  let sy = Math.min(finalY + 22, pageH - 30);

  doc.setFont(FONT_FAMILY, 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(30);

  const signedBy = supplier?.contactPerson?.trim() || supplier?.legalName?.trim() || supplier?.name || '';
  const colW = contentW / 2;
  const drawSign = (label: string, hint: string, x: number) => {
    doc.text(label, x, sy);
    const lw = doc.getTextWidth(label) + 2;
    doc.setDrawColor(120);
    doc.line(x + lw, sy + 0.5, x + colW - 6, sy + 0.5);
    if (hint) {
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(hint, x + lw + 2, sy + 4);
      doc.setFontSize(10.5);
      doc.setTextColor(30);
    }
  };
  drawSign('Сдал', signedBy, margin);
  drawSign('Принял', '', margin + colW);
  sy += 10;

  return doc;
}

// ── Действия ─────────────────────────────────────────────────────────────────
export async function downloadInvoice(
  order: SupplierOrder,
  supplier: SupplierProfile | null,
): Promise<void> {
  const doc = await buildInvoice(order, supplier);
  doc.save(invoiceFileName(order));
}

export type ShareResult = 'shared' | 'downloaded';

// Web Share API с файлом (PWA на телефоне). Если недоступно — скачиваем.
export async function shareInvoice(
  order: SupplierOrder,
  supplier: SupplierProfile | null,
): Promise<ShareResult> {
  const doc = await buildInvoice(order, supplier);
  const blob = doc.output('blob') as Blob;
  const name = invoiceFileName(order);
  const file = new File([blob], name, { type: 'application/pdf' });

  const nav = navigator as Navigator & { canShare?: (d?: ShareData) => boolean };
  if (typeof nav.share === 'function' && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: `Накладная ${invoiceNo(order)}` });
      return 'shared';
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return 'shared';
      // прочие ошибки — падаем в скачивание
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}
